import React, { useState, useEffect } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td, Select, Input, Button } from '@chakra-ui/react';
import fetchWrapper from '../lib/fetch-wrapper';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../lib/profileContext';
import * as XLSX from 'xlsx';

const UserInteractionsTable = () => {
    const { instance, accounts }: { instance: IPublicClientApplication, accounts: AccountInfo[] } = useMsal();
    const { profile } = useProfile();
    const [interactions, setInteractions] = useState([]);
    const [filters, setFilters] = useState({
        UserSessionId: '',
        User: '',
        Property: '',
        CreatedOnUtc: '',
    });
    const [filterOptions, setFilterOptions] = useState({ Users: [], Properties: [] });
    const fw = new fetchWrapper();

    const fetchInteractions = async () => {
        try {
            const response = await fw.getInteractions('/api/get-user-interactions', filters);
            setInteractions(response.data);
        } catch (error) {
            console.error('Error fetching interactions:', error);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const [usersResponse, propertiesResponse] = await Promise.all([
                fw.getInteractionsFilters('/api/get-filter-values', 'User'),
                fw.getInteractionsFilters('/api/get-filter-values', 'Property'),
            ]);
            setFilterOptions({
                Users: usersResponse.data,
                Properties: propertiesResponse.data,
            });
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const handleExportToExcel = () => {
        if (interactions.length === 0) {
            alert('No data to export!');
            return;
        }

        const excelData = interactions.map((interaction: any) => ({
            Question: interaction.Question,
            Answer: interaction.Answer,
            'Created On': new Date(interaction.CreatedOnUtc).toLocaleString(),
            User: interaction.User,
            Property: interaction.Property,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'User Interactions');

        XLSX.writeFile(workbook, 'user_interactions.xlsx');
    };

    useEffect(() => {
        void fetchInteractions();
        void fetchFilterOptions();
    }, []);

    const handleFilterChange = (e: any) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    return (
        <div style={{ height: '100%', overflowY: 'auto' }}>
            <div style={{ marginBottom: '20px' }}>
                <Select
                    name="User"
                    aria-label="User"
                    value={filters.User}
                    onChange={(e) => setFilters({ ...filters, User: e.target.value })}
                >
                    <option value="">Filter by User</option>
                    {filterOptions.Users.map((user) => (
                        <option key={user} value={user}>{user}</option>
                    ))}
                </Select>
                <Select
                    name="Property"
                    aria-label="Property"
                    value={filters.Property}
                    onChange={(e) => setFilters({ ...filters, Property: e.target.value })}
                >
                    <option value="">Filter by Property</option>
                    {filterOptions.Properties.map((property) => (
                        <option key={property} value={property}>{property}</option>
                    ))}
                </Select>
                <Input
                    type="date"
                    name="CreatedOnUtc"
                    onChange={handleFilterChange}
                    placeholder="Filter by Date"
                />
                <Button variant="brand" onClick={fetchInteractions} style={{ marginRight: '10px' }}>
                    Apply Filters
                </Button>
                <Button variant="brand" onClick={handleExportToExcel}>
                    Export to Excel
                </Button>
            </div>
            <Table variant="simple">
                <Thead>
                    <Tr>
                        <Th>Question</Th>
                        <Th>Answer</Th>
                        <Th>Created On</Th>
                        <Th>User</Th>
                        <Th>Property</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {interactions.map((interaction: any) => (
                        <Tr key={interaction.UserInteractionId}>
                            <Td>{interaction.Question}</Td>
                            <Td>{interaction.Answer}</Td>
                            <Td>{new Date(interaction.CreatedOnUtc).toLocaleString()}</Td>
                            <Td>{interaction.User}</Td>
                            <Td>{interaction.Property}</Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </div>
    );
};

export default UserInteractionsTable;
